import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors } from '~/styles';
import downloadRecordsetCsv from './downloadCsv';

const S = {
  Block: styled.div`
    border: 1px solid ${colors.grey10};
    border-radius: 0.5rem;
    margin-bottom: 0.75rem;
    width: 100%;
  `,

  Header: styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    cursor: pointer;

    &:hover {
      background: ${colors.grey5};
    }
  `,

  HeaderChevron: styled.span`
    font-size: 0.625rem;
    color: ${colors.grey40};
    width: 0.75rem;
  `,

  HeaderText: styled.span`
    font-size: 0.8125rem;
    color: ${colors.grey55};
  `,

  Body: styled.div`
    position: relative;
    padding: 0 0.75rem 0.75rem 0.75rem;
  `,

  ConnectingLine: styled.div`
    position: absolute;
    left: 1rem;
    top: 0;
    bottom: 1.5rem;
    width: 1px;
    background: ${colors.grey5};
  `,

  Step: styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.25rem 0;

    &.nested {
      padding-left: 1.25rem;
    }

    &:hover button {
      opacity: 1;
    }
  `,

  StepIcon: styled.span`
    width: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.white};
    font-size: 0.625rem;

    &.running {
      color: ${colors.primary};
      animation: spin 1s linear infinite;
    }
    &.completed {
      color: ${colors.success};
    }
    &.failed {
      color: ${colors.danger};
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,

  StepText: styled.span`
    font-size: 0.8125rem;
    color: ${colors.grey55};
    line-height: 1.4;
  `,

  DownloadButton: styled.button`
    flex-shrink: 0;
    background: none;
    border: none;
    border-radius: 0;
    padding: 0;
    height: auto;
    color: ${colors.grey40};
    font-size: 0.6875rem;
    cursor: pointer;
    opacity: 0;
    transition:
      opacity 0.1s ease,
      color 0.1s ease;

    &:hover {
      background: none;
      color: ${colors.primary};
    }
  `,
};

// A program's queries arrive as children of its step (parentCallId). A child whose parent never
// arrived falls back to the top level.
function orderByParent(tools) {
  const byParent = new Map();
  for (const tool of tools) {
    if (!tool.parentCallId) continue;
    if (!byParent.has(tool.parentCallId)) byParent.set(tool.parentCallId, []);
    byParent.get(tool.parentCallId).push(tool);
  }
  const seen = new Set();
  const ordered = [];
  for (const tool of tools) {
    if (tool.parentCallId && tools.some((t) => t.callId === tool.parentCallId)) continue;
    if (seen.has(tool.callId)) continue;
    seen.add(tool.callId);
    ordered.push(tool);
    for (const child of byParent.get(tool.callId) ?? []) {
      if (seen.has(child.callId)) continue;
      seen.add(child.callId);
      ordered.push(child);
    }
  }
  return ordered;
}

export default function ToolStatusBar({ tools, downloads = {}, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const runningCount = tools.filter((t) => t.status === 'running').length;
  const completedCount = tools.filter((t) => t.status === 'completed').length;
  const failedCount = tools.filter((t) => t.status === 'failed').length;
  const totalCount = tools.length;
  const allComplete = runningCount === 0 && totalCount > 0;
  const ordered = orderByParent(tools);

  let headerText;
  if (allComplete) {
    headerText = `${completedCount} step${completedCount !== 1 ? 's' : ''} completed${failedCount > 0 ? `, ${failedCount} failed` : ''}`;
  } else {
    headerText = `${totalCount} step${totalCount !== 1 ? 's' : ''}`;
  }

  if (tools.length === 0) return null;

  return (
    <S.Block data-testid="tool-execution-block">
      <S.Header data-testid="tool-execution-header" onClick={() => setExpanded(!expanded)}>
        <S.HeaderChevron>
          <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </S.HeaderChevron>
        {allComplete ? (
          <Icon icon="circle-check" color={colors.success} style={{ fontSize: '0.75rem' }} />
        ) : (
          <Icon icon="spinner" color={colors.grey40} style={{ fontSize: '0.75rem' }} spin />
        )}
        <S.HeaderText>{headerText}</S.HeaderText>
      </S.Header>

      {expanded && (
        <S.Body data-testid="tool-execution-body">
          <S.ConnectingLine />
          {ordered.map((tool) => {
            const download = downloads[tool.callId];
            const nested = Boolean(tool.parentCallId && tools.some((t) => t.callId === tool.parentCallId));
            return (
              <S.Step
                key={tool.callId}
                className={nested ? 'nested' : undefined}
                data-testid="tool-step"
                data-nested={nested || undefined}
                data-status={tool.status}>
                <S.StepIcon className={tool.status}>
                  <Icon
                    icon={tool.status === 'running' ? 'spinner' : tool.status === 'completed' ? 'check' : 'xmark'}
                  />
                </S.StepIcon>
                <S.StepText>{tool.statusText}</S.StepText>
                {download && (
                  <S.DownloadButton
                    type="button"
                    data-testid="tool-step-download"
                    title="Download CSV"
                    aria-label="Download CSV"
                    onClick={() => downloadRecordsetCsv(download)}>
                    <Icon icon="download" />
                  </S.DownloadButton>
                )}
              </S.Step>
            );
          })}
        </S.Body>
      )}
    </S.Block>
  );
}
